import type { SortingQuery } from './sort-info.type'
import { extractQuery } from '../utils'
import { z } from '../utils/zod-extend'
import { stringArrayParamSchema } from './http-param.type'
import { campaignLogicSingleSchema } from './logic/campaign-logic.type'
import { offsetPaginationParamSchema } from './page-info.type'
import { sortingParamSchema } from './sort-info.type'
import { fileQuestionUsageSchema } from './survey-json.type'

// 配信終了条件
export const endConditionTypes = ['none', 'whenCompleted'] as const
export type EndConditionType = typeof endConditionTypes[number]

export const campaignAssetConfigSchema = z.object({
  imageUrl: z.string().max(2048).optional(),
  imageUid: z.string().max(255).optional(),
})
export type CampaignAssetConfig = z.infer<typeof campaignAssetConfigSchema>

export const campaignLogicConfigSchema = z.object({
  logics: z.array(campaignLogicSingleSchema),
})

export type CampaignLogicConfig = z.infer<typeof campaignLogicConfigSchema>

export const campaignDeliverySchema = z.object({
  type: z.literal('delivery'),
  endCondition: z.enum(endConditionTypes),
  startAt: z.coerce.date().nullable(),
  endAt: z.coerce.date().nullable(),
  enabled: z.boolean(),
  enableLoginUser: z.boolean(),
  enableAnonymousUser: z.boolean(),
})

/**
 * client form専用 schema
 * - transform & refine
 */
export const campaignDeliveryClientFormSchema = campaignDeliverySchema
  .refine(
    v => !!v.startAt,
    {
      message: '開始日を指定してください',
      path: ['startAt'],
    },
  ).refine(
    v => !!v.endAt,
    {
      message: '終了日を指定してください',
      path: ['endAt'],
    },
  ).refine(
    (v) => {
      if (!v.startAt || !v.endAt) {
        return true
      }
      return v.startAt < v.endAt
    },
    {
      message: '終了日時は開始日時より後の日付を設定してください',
      path: ['endAt'],
    },
  )

export type CampaignDeliveryFormValues = z.infer<typeof campaignDeliveryClientFormSchema>

export const campaignSurveyFormSchema = z.object({
  type: z.literal('survey'),
  name: z.string().trim().min(1).max(256),
  surveyId: z.string().nullable(),
  description: z.string().max(2048),
  tagId: z.string().min(1),
  mypageDisplayQuestionId: z.string().optional(),
  gameStampRally: z.object({
    mediaUid: z.string().max(36).nullish(),
    imageUrl: z.string().max(2048).nullish(),
    stampImageQuestionId: z.string().max(36).nullish(),
  }),
})
export type CampaignSurveyFormValues = z.infer<typeof campaignSurveyFormSchema>

export const campaignLauncherContentFormSchema = z.object({
  type: z.literal('thanks-content'),
  launcherContentId: z.string().nullable(),
})
export type CampaignLauncherContentFormValues = z.infer<typeof campaignLauncherContentFormSchema>

export const createCampaignSchema = campaignSurveyFormSchema
export type CreateCampaignValues = z.infer<typeof createCampaignSchema>

export const updateCampaignSchema = z.discriminatedUnion('type', [
  campaignDeliverySchema,
  campaignSurveyFormSchema,
  campaignLauncherContentFormSchema,
])
export type UpdateCampaignValues = z.infer<typeof updateCampaignSchema>

// campaign logic update
export const updateCampaignLogicConfigSchema = z.object({
  campaignLogicJson: campaignLogicConfigSchema,
})
export type UpdateCampaignLogicConfigValues = z.infer<typeof updateCampaignLogicConfigSchema>

export const campaignQueryPresets = ['all', 'delivery', 'deliveryScheduled'] as const
export type CampaignQueryPresets = typeof campaignQueryPresets[number]

export const isCampaignPresets = (v: any): v is CampaignQueryPresets => campaignQueryPresets.includes(v)

export const campaignDeliverySorts: SortingQuery = {
  sortFields: ['startAt', 'updatedAt'],
  sortDirections: ['asc', 'asc'],
}
export const campaignDeliverySortConditions = extractQuery(campaignDeliverySorts).sorts

export const searchCampaignQuerySchema = z.object({
  preset: z.enum(campaignQueryPresets).optional(),
  name: z.string().optional(),
  ids: stringArrayParamSchema.optional(),
  excludeIds: stringArrayParamSchema.optional(),
  surveyIds: stringArrayParamSchema.optional(),
  tagIds: stringArrayParamSchema.optional(),
  launcherContentIds: stringArrayParamSchema.optional(),
  fileQuestionUsage: fileQuestionUsageSchema.optional(),
}).merge(sortingParamSchema).openapi('SearchCampaignQuery')
export type SearchCampaignQuery = z.infer<typeof searchCampaignQuerySchema>

export const searchCampaignWithOffsetQuerySchema = searchCampaignQuerySchema.merge(offsetPaginationParamSchema)
export type SearchCampaignWithOffsetQuery = z.infer<typeof searchCampaignWithOffsetQuerySchema>
